/**
 * demo/viz.js
 *
 * All D3/canvas drawing logic. No SVG overlays — eliminates all
 * positioning/stretching complexity.
 *
 * Canvas (waveform, spectrum): DPR-aware via getCtx(). D3 scales are
 * used as pure math helpers (no DOM connection here).
 *
 * SVG (param curves, receptor bars): D3 owns these elements fully.
 */

import * as d3 from "d3";

// ─── constants ────────────────────────────────────────────────────────────────

export const BG = "#05101f";
export const MONO = "'IBM Plex Mono', 'Courier New', monospace";
const FMIN = 2,
    FMAX = 1200;

// Mechanoreceptor channel definitions
// Refs: Verrillo (1963, 1965 JASA); Mountcastle et al. (1967 J.Neurophysiol);
//       Gescheider et al. (1990 JASA); Bensmaia & Hollins (2003 Somatosens.Mot.Res.)
export const BANDS = [
    {
        id: "sa",
        label: "SA",
        sub: "Merkel / Ruffini",
        fMin: 0.5,
        fMax: 16,
        color: "#a78bfa",
    },
    {
        id: "ra1",
        label: "RA1",
        sub: "Meissner",
        fMin: 16,
        fMax: 50,
        color: "#38bdf8",
    },
    {
        id: "pc",
        label: "PC",
        sub: "Pacinian",
        fMin: 50,
        fMax: 700,
        color: "#34d399",
    },
];

// ─── DPR-aware canvas context ─────────────────────────────────────────────────

/**
 * getCtx(canvas) → { ctx, W, H }
 *
 * W and H are CSS pixel dimensions (device-independent).
 * The physical buffer is resized to W*dpr × H*dpr whenever dimensions change.
 * ctx.setTransform resets to dpr×dpr scale every frame so all draw calls use
 * CSS pixel coordinates — same space as the D3 scales.
 *
 * This is the single fix for blurry canvas on hi-DPI displays.
 */
export function getCtx(canvas) {
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth || 600;
    const H = canvas.offsetHeight || 200;
    const pw = Math.round(W * dpr);
    const ph = Math.round(H * dpr);

    if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
    }

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
    return { ctx, W, H };
}

// ─── canvas micro-helpers ─────────────────────────────────────────────────────

function t(
    ctx,
    s,
    x,
    y,
    {
        color = "rgba(255,255,255,0.28)",
        size = 9,
        align = "left",
        bold = false,
    } = {},
) {
    ctx.fillStyle = color;
    ctx.font = `${bold ? "bold " : ""}${size}px ${MONO}`;
    ctx.textAlign = align;
    ctx.textBaseline = "middle";
    ctx.fillText(s, x, y);
    ctx.textAlign = "left";
}

function hl(ctx, y, x0, x1, color = "rgba(255,255,255,0.05)", dash = []) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash(dash);
    ctx.beginPath();
    ctx.moveTo(x0, y);
    ctx.lineTo(x1, y);
    ctx.stroke();
    ctx.setLineDash([]);
}

function vl(ctx, x, y0, y1, color = "rgba(255,255,255,0.05)", dash = []) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash(dash);
    ctx.beginPath();
    ctx.moveTo(x, y0);
    ctx.lineTo(x, y1);
    ctx.stroke();
    ctx.setLineDash([]);
}

// ─── waveform ─────────────────────────────────────────────────────────────────

export function drawWave(canvas, analyser) {
    const { ctx, W, H } = getCtx(canvas);
    const ml = 42,
        mr = 10,
        mt = 22,
        mb = 26;
    const pw = W - ml - mr,
        ph = H - mt - mb;

    const xSc = d3
        .scaleLinear()
        .domain([0, 1])
        .range([ml, W - mr]);
    const ySc = d3
        .scaleLinear()
        .domain([-1, 1])
        .range([H - mb, mt]);

    // Grid
    hl(ctx, ySc(-0.5), ml, W - mr);
    hl(ctx, ySc(0.5), ml, W - mr);
    hl(ctx, ySc(0), ml, W - mr, "rgba(255,255,255,0.10)", [4, 4]);

    // Y axis labels
    [-1, -0.5, 0, 0.5, 1].forEach((v) => {
        ctx.strokeStyle = "rgba(255,255,255,0.10)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(ml - 4, ySc(v));
        ctx.lineTo(ml, ySc(v));
        ctx.stroke();
        t(ctx, v.toFixed(1), ml - 6, ySc(v), { align: "right", size: 8 });
    });

    ctx.strokeStyle = "rgba(255,255,255,0.07)";
    ctx.lineWidth = 1;
    ctx.strokeRect(ml, mt, pw, ph);

    t(ctx, "WAVEFORM", ml + 8, mt + 9, {
        color: "rgba(103,232,249,0.55)",
        size: 10,
    });

    if (!analyser) {
        t(ctx, "▶  PRESS PLAY", W / 2, H / 2, {
            color: "rgba(255,255,255,0.12)",
            size: 11,
            align: "center",
        });
        return;
    }

    const N = Math.min(analyser.fftSize, 2048);
    const raw = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(raw);

    const line = d3
        .line()
        .x((_, i) => xSc(i / N))
        .y((d) => ySc(d / 128 - 1))
        .context(ctx);

    ctx.save();
    ctx.beginPath();
    ctx.rect(ml, mt, pw, ph);
    ctx.clip();
    ctx.beginPath();
    line(Array.from(raw).slice(0, N));
    ctx.strokeStyle = "#67e8f9";
    ctx.lineWidth = 1.5;
    ctx.shadowColor = "#67e8f9";
    ctx.shadowBlur = 6;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
}

// ─── spectrum ─────────────────────────────────────────────────────────────────

export function drawSpectrum(canvas, analyser, actx, oscFreqs) {
    const { ctx, W, H } = getCtx(canvas);
    const ml = 46,
        mr = 10,
        mt = 28,
        mb = 30;
    const pw = W - ml - mr,
        ph = H - mt - mb;

    const xSc = d3
        .scaleLog()
        .domain([FMIN, FMAX])
        .range([ml, W - mr])
        .clamp(true);
    const ySc = d3
        .scaleLinear()
        .domain([-90, 0])
        .range([H - mb, mt]);

    // Mechanoreceptor band fills + labels
    BANDS.forEach((b) => {
        const x0 = xSc(Math.max(b.fMin, FMIN)),
            x1 = xSc(Math.min(b.fMax, FMAX));
        ctx.fillStyle = b.color + "10";
        ctx.fillRect(x0, mt, x1 - x0, ph);
        vl(ctx, x0, mt, H - mb, b.color + "22");
        t(ctx, b.label, x0 + 4, mt + 11, {
            color: b.color,
            size: 10,
            bold: true,
        });
        t(ctx, b.sub, x0 + 4, mt + 22, { color: b.color + "88", size: 7.5 });
    });

    // dB grid
    [-72, -54, -36, -18].forEach((db) => {
        hl(ctx, ySc(db), ml, W - mr);
        t(ctx, db + " dB", ml - 4, ySc(db), { align: "right", size: 8 });
    });

    // Hz ticks
    [4, 10, 20, 50, 100, 200, 500, 1000].forEach((f) => {
        const x = xSc(f);
        if (x < ml || x > W - mr) return;
        vl(ctx, x, mt, H - mb);
        t(ctx, f >= 1000 ? f / 1000 + "k" : String(f), x, H - mb + 12, {
            align: "center",
            size: 8,
        });
    });
    t(ctx, "Hz", W - mr, H - mb + 12, {
        color: "rgba(255,255,255,0.18)",
        size: 8,
        align: "right",
    });

    // Oscillator bank markers
    oscFreqs?.forEach((f) =>
        vl(
            ctx,
            xSc(Math.max(f, FMIN)),
            mt,
            H - mb,
            "rgba(251,191,36,0.28)",
            [2, 4],
        ),
    );

    ctx.strokeStyle = "rgba(255,255,255,0.07)";
    ctx.lineWidth = 1;
    ctx.strokeRect(ml, mt, pw, ph);

    t(ctx, "SPECTRUM  (log Hz · dBFS)", ml + 4, mt - 12, {
        color: "rgba(147,197,253,0.55)",
        size: 10,
    });

    if (!analyser || !actx) {
        t(ctx, "▶  PRESS PLAY", W / 2, H / 2, {
            color: "rgba(255,255,255,0.12)",
            size: 11,
            align: "center",
        });
        return;
    }

    const buf = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(buf);
    const binHz = actx.sampleRate / analyser.fftSize;
    const pts = [];
    for (let i = 1; i < buf.length; i++) {
        const f = i * binHz;
        if (f < FMIN) continue;
        if (f > FMAX * 1.05) break;
        pts.push([f, (buf[i] / 255) * 90 - 90]);
    }
    if (pts.length < 2) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(ml, mt, pw, ph);
    ctx.clip();

    // Gradient area fill — d3.area() writes directly to canvas context
    const area = d3
        .area()
        .x((d) => xSc(d[0]))
        .y0(H - mb)
        .y1((d) => ySc(d[1]))
        .context(ctx);
    const grad = ctx.createLinearGradient(0, mt, 0, H - mb);
    grad.addColorStop(0, "rgba(96,165,250,0.22)");
    grad.addColorStop(1, "rgba(96,165,250,0.01)");
    ctx.beginPath();
    area(pts);
    ctx.fillStyle = grad;
    ctx.fill();

    // Spectrum line
    const line = d3
        .line()
        .x((d) => xSc(d[0]))
        .y((d) => ySc(d[1]))
        .context(ctx);
    ctx.beginPath();
    line(pts);
    ctx.strokeStyle = "#93c5fd";
    ctx.lineWidth = 1.6;
    ctx.shadowColor = "#93c5fd";
    ctx.shadowBlur = 5;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
}

// ─── band energy extraction ───────────────────────────────────────────────────

export function bandEnergies(analyser, actx) {
    if (!analyser || !actx) return BANDS.map(() => 0);
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);
    const binHz = actx.sampleRate / analyser.fftSize;
    return BANDS.map((b) => {
        const i0 = Math.max(1, Math.floor(b.fMin / binHz));
        const i1 = Math.min(data.length - 1, Math.ceil(b.fMax / binHz));
        let sum = 0,
            n = 0;
        for (let i = i0; i <= i1; i++) {
            sum += data[i];
            n++;
        }
        return n > 0 ? sum / n / 255 : 0;
    });
}

// ─── D3 axis style helper ─────────────────────────────────────────────────────

const styleAxis = (g) => {
    g.select(".domain").attr("stroke", "rgba(255,255,255,0.10)");
    g.selectAll(".tick line").attr("stroke", "rgba(255,255,255,0.10)");
    g.selectAll(".tick text")
        .attr("fill", "rgba(255,255,255,0.28)")
        .attr("font-size", 9)
        .attr("font-family", MONO);
};

// ─── param mapping curves (D3 SVG) ───────────────────────────────────────────

export function drawParamCurves(svgEl, voiceMeta, params, liveVal) {
    if (!svgEl || !params || !voiceMeta) return;
    const W = svgEl.clientWidth || 260;
    const H = 192;
    const M = { t: 28, r: 14, b: 30, l: 42 };
    const curves = voiceMeta.curves(params);

    const svg = d3
        .select(svgEl)
        .attr("viewBox", `0 0 ${W} ${H}`)
        .attr("width", "100%")
        .attr("height", H);
    svg.selectAll("*").remove();
    svg.append("rect").attr("width", W).attr("height", H).attr("fill", BG);

    const xs = d3
        .scaleLinear()
        .domain([0, 1])
        .range([M.l, W - M.r]);
    const ys = d3
        .scaleLinear()
        .domain([0, 1.07])
        .range([H - M.b, M.t]);

    svg.append("g")
        .selectAll("line")
        .data([0.25, 0.5, 0.75, 1.0])
        .join("line")
        .attr("x1", M.l)
        .attr("x2", W - M.r)
        .attr("y1", (d) => ys(d))
        .attr("y2", (d) => ys(d))
        .attr("stroke", "rgba(255,255,255,0.04)");

    if (params.threshold != null) {
        const xt = xs(params.threshold);
        svg.append("line")
            .attr("x1", xt)
            .attr("x2", xt)
            .attr("y1", M.t)
            .attr("y2", H - M.b)
            .attr("stroke", "rgba(255,255,255,0.18)")
            .attr("stroke-dasharray", "3 4");
        svg.append("text")
            .attr("x", xt + 3)
            .attr("y", M.t + 10)
            .attr("fill", "rgba(255,255,255,0.3)")
            .attr("font-size", 9)
            .attr("font-family", MONO)
            .text("τ=" + params.threshold.toFixed(2));
    }

    // Glow filter
    const flt = svg.append("defs").append("filter").attr("id", "cglow");
    flt.append("feGaussianBlur").attr("stdDeviation", 3).attr("result", "b");
    const fm = flt.append("feMerge");
    fm.append("feMergeNode").attr("in", "b");
    fm.append("feMergeNode").attr("in", "SourceGraphic");

    const pts = d3.range(0, 1.005, 0.005);
    const lineGen = d3
        .line()
        .x((d) => xs(d[0]))
        .y((d) => ys(d[1]));

    curves.forEach((c) => {
        svg.append("path")
            .datum(pts.map((x) => [x, c.fn(x)]))
            .attr("d", lineGen)
            .attr("fill", "none")
            .attr("stroke", c.color)
            .attr("stroke-width", 1.8)
            .attr("opacity", 0.9);
    });

    if (liveVal != null) {
        const xv = xs(liveVal),
            y0 = ys(curves[0].fn(liveVal));
        svg.append("line")
            .attr("x1", xv)
            .attr("x2", xv)
            .attr("y1", H - M.b)
            .attr("y2", y0)
            .attr("stroke", "rgba(251,191,36,0.38)")
            .attr("stroke-dasharray", "2 3");
        svg.append("line")
            .attr("x1", M.l)
            .attr("x2", xv)
            .attr("y1", y0)
            .attr("y2", y0)
            .attr("stroke", "rgba(251,191,36,0.38)")
            .attr("stroke-dasharray", "2 3");
        curves.forEach((c) => {
            svg.append("circle")
                .attr("cx", xv)
                .attr("cy", ys(c.fn(liveVal)))
                .attr("r", 4)
                .attr("fill", "#fbbf24")
                .attr("filter", "url(#cglow)");
        });
    }

    svg.append("g")
        .attr("transform", `translate(0,${H - M.b})`)
        .call(d3.axisBottom(xs).ticks(5))
        .call(styleAxis);
    svg.append("g")
        .attr("transform", `translate(${M.l},0)`)
        .call(d3.axisLeft(ys).ticks(4).tickFormat(d3.format(".1f")))
        .call(styleAxis);

    curves.forEach((c, i) => {
        const g = svg
            .append("g")
            .attr("transform", `translate(${M.l + 4},${M.t + 3 + i * 13})`);
        g.append("line")
            .attr("x2", 10)
            .attr("y1", 5)
            .attr("y2", 5)
            .attr("stroke", c.color)
            .attr("stroke-width", 1.5);
        g.append("text")
            .attr("x", 13)
            .attr("y", 5)
            .attr("dominant-baseline", "middle")
            .attr("fill", c.color)
            .attr("font-size", 9)
            .attr("font-family", MONO)
            .text(c.label);
    });

    svg.append("text")
        .attr("x", M.l + 4)
        .attr("y", 16)
        .attr("fill", "rgba(255,255,255,0.32)")
        .attr("font-size", 10)
        .attr("font-family", MONO)
        .text("PARAM MAPPING");
    svg.append("text")
        .attr("x", W - M.r)
        .attr("y", H - 4)
        .attr("text-anchor", "end")
        .attr("fill", "rgba(255,255,255,0.2)")
        .attr("font-size", 9)
        .attr("font-family", MONO)
        .text("← " + (voiceMeta.curveXLabel ?? "parameter"));
}

// ─── receptor activation bars (D3 SVG, transitions) ──────────────────────────

export function initReceptorBars(svgEl) {
    if (!svgEl) return;
    const W = svgEl.clientWidth || 260;
    const H = 112;
    const ML = 68,
        MR = 8,
        MT = 22,
        MB = 8;
    const trackW = W - ML - MR;

    const svg = d3
        .select(svgEl)
        .attr("viewBox", `0 0 ${W} ${H}`)
        .attr("width", "100%")
        .attr("height", H);
    svg.selectAll("*").remove();
    svg.append("rect").attr("width", W).attr("height", H).attr("fill", BG);

    const ySc = d3
        .scaleBand()
        .domain(BANDS.map((b) => b.id))
        .range([MT, H - MB])
        .padding(0.32);

    svg.append("text")
        .attr("x", ML + 2)
        .attr("y", 15)
        .attr("fill", "rgba(255,255,255,0.32)")
        .attr("font-size", 10)
        .attr("font-family", MONO)
        .text("RECEPTOR ACTIVATION");

    BANDS.forEach((b) => {
        svg.append("rect")
            .attr("x", ML)
            .attr("y", ySc(b.id))
            .attr("width", trackW)
            .attr("height", ySc.bandwidth())
            .attr("fill", "rgba(255,255,255,0.04)")
            .attr("rx", 2);
        svg.append("text")
            .attr("x", ML - 4)
            .attr("y", ySc(b.id) + ySc.bandwidth() / 2)
            .attr("dominant-baseline", "middle")
            .attr("text-anchor", "end")
            .attr("fill", b.color)
            .attr("font-size", 10)
            .attr("font-weight", "bold")
            .attr("font-family", MONO)
            .text(b.label);
        svg.append("text")
            .attr("x", 2)
            .attr("y", ySc(b.id) + ySc.bandwidth() / 2)
            .attr("dominant-baseline", "middle")
            .attr("fill", b.color + "55")
            .attr("font-size", 7.5)
            .attr("font-family", MONO)
            .text(b.sub);
        svg.append("rect")
            .attr("class", `rbar-${b.id}`)
            .attr("x", ML)
            .attr("y", ySc(b.id))
            .attr("width", 0)
            .attr("height", ySc.bandwidth())
            .attr("fill", b.color)
            .attr("rx", 2);
    });

    svgEl._rxSc = d3.scaleLinear().domain([0, 1]).range([0, trackW]);
}

export function updateReceptorBars(svgEl, energies) {
    if (!svgEl?._rxSc) return;
    const svg = d3.select(svgEl);
    BANDS.forEach((b, i) => {
        svg.select(`.rbar-${b.id}`)
            .transition()
            .duration(60)
            .ease(d3.easeLinear)
            .attr("width", svgEl._rxSc(Math.min(energies[i] ?? 0, 1)));
    });
}
